import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import orderService from "../services/allOrders";
import {
  addSaga,
  createAsyncSaga,
  createSagaReducer,
  persist,
  sagaSlice,
} from "./sagaStore";
import type {
  AsyncEntry,
  ShopConfig,
  ShopDetails,
} from "../types/shopConfig";

export type { ShopConfig, ShopDetails } from "../types/shopConfig";

interface OrdersListState {
  loading: boolean;
  error: string | null;
  data: Record<string, unknown>;
}

interface OrderDetailsState {
  prefilledData: Record<string, unknown>;
  orderApiData: Record<string, unknown>;
  orderIdData: string[];
  orderStatuses: unknown[];
  cancelReturnReason: Record<string, unknown>;
  orderStatusDetails: Record<string, unknown>;
  customerAddresses: Record<string, unknown>[];
}

interface SelectedProductDetails {
  id?: string;
  value?: string;
  label?: string;
  price?: number;
  mrp?: number;
  image?: string;
  quantity?: number;
  unit?: string;
}

export interface AllOrderManagementState {
  list: OrdersListState;
  orderDetails: OrderDetailsState;
  /** Per-shop cache for shop config. Keyed by shopId. */
  configByShopId: Record<string, AsyncEntry<ShopConfig>>;
  /** Per-shop cache for shop details. Keyed by shopId. */
  shopDetailsByShopId: Record<string, AsyncEntry<ShopDetails>>;
  state: string | null;
  externalOrderId: string | null;
  bapId: string | null;
  shopName: string | null;
  deliveryPincode: string | null;
  paymentTransactionId: string | null;
  shopType: string | null;
  startDate: string | null;
  endDate: string | null;
  isSettled: boolean | null;
  isOndcFaSettled: boolean | null;
  deliveryStatus: string | null;
  paymentType: string | null;
  paymentMode: string | null;
  customerName: string | null;
  customerPhone: string | null;
  deliveryUserId: string | null;
  isHeavyItemPresent: boolean | null;
  isDeliveryLocationUpdated: boolean | null;
  itemId: string | null;
  rating: string | null;
  selectedProductDetails: SelectedProductDetails | null;
  created_at: string | null;
  modified_at: string | null;
  shopId: string | null;
  isFilterApplied: boolean;
  isDateFilterApplied: boolean;
  currentPage: string;
  selectedRowKeys: Array<string | number>;
  deliveryAgents: Record<string, { value: string; label: string }[]>;
  newOrdersCount: number;
  searchTerm: string | null;
}

const initialState: AllOrderManagementState = {
  list: {
    loading: false,
    error: null,
    data: {},
  },
  orderDetails: {
    prefilledData: {},
    orderApiData: {},
    orderIdData: [],
    orderStatuses: [],
    cancelReturnReason: {},
    orderStatusDetails: {},
    customerAddresses: [],
  },
  configByShopId: {},
  shopDetailsByShopId: {},
  state: null,
  externalOrderId: null,
  bapId: null,
  shopName: null,
  deliveryPincode: null,
  paymentTransactionId: null,
  shopType: null,
  startDate: null,
  endDate: null,
  isSettled: null,
  isOndcFaSettled: null,
  deliveryStatus: null,
  paymentType: null,
  paymentMode: null,
  customerName: null,
  customerPhone: null,
  deliveryUserId: null,
  isHeavyItemPresent: null,
  isDeliveryLocationUpdated: null,
  itemId: null,
  rating: null,
  selectedProductDetails: null,
  created_at: null,
  modified_at: null,
  shopId: null,
  isFilterApplied: false,
  isDateFilterApplied: false,
  currentPage: "1",
  selectedRowKeys: [],
  deliveryAgents: {},
  newOrdersCount: 0,
  searchTerm: null,
};

const Slice: sagaSlice = createSlice({
  ...persist("allOrderManagement", initialState),
  reducers: {
    ...createSagaReducer("fetchAllOrders", "list"),
    // Overrides the generic pending reducer: `silent` refreshes (used while
    // reconciling just-created orders) keep the current list and counts on
    // screen instead of clearing them. The saga ignores the extra flag.
    fetchAllOrders: (state, action) => {
      state.list.error = null;
      if (action.payload?.silent) return;
      state.list.loading = true;
      state.list.data = {};
    },
    setPreFilledData: (state, action) => {
      state.orderDetails.prefilledData = action.payload;
    },
    setOrderApiData: (state, action) => {
      state.orderDetails.orderApiData = action.payload;
    },
    updateState(state, action) {
      state.state = action.payload;
    },
    updateExternalOrderId(state, action) {
      state.externalOrderId = action.payload;
    },
    updateBapId(state, action) {
      state.bapId = action.payload;
    },
    updateShopName(state, action) {
      state.shopName = action.payload;
    },
    updateDeliveryPincode(state, action) {
      state.deliveryPincode = action.payload;
    },
    updatePaymentTransactionId(state, action) {
      state.paymentTransactionId = action.payload;
    },
    updateShopType(state, action) {
      state.shopType = action.payload;
    },
    updateStartDate(state, action) {
      state.startDate = action.payload;
    },
    updateEndDate(state, action) {
      state.endDate = action.payload;
    },
    updateCreatedAt(state, action) {
      state.created_at = action.payload;
    },
    updateModifiedAt(state, action) {
      state.modified_at = action.payload;
    },
    updateFilterButton(state, action) {
      state.isFilterApplied = action.payload;
    },
    updateOrderIdData(state, action) {
      state.orderDetails.orderIdData = action.payload;
    },
    updateDateFilterButton(state, action) {
      state.isDateFilterApplied = action.payload;
    },
    updateOrderStatuses(state, action) {
      state.orderDetails.orderStatuses = action.payload;
    },
    updateCancelReturnReason(state, action) {
      state.orderDetails.cancelReturnReason = action.payload;
    },
    updateIsSettled(state, action) {
      state.isSettled = action.payload;
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setSelectedRowKeys(state, action) {
      state.selectedRowKeys = action.payload.length === 0 ? [] : action.payload;
    },
    updateIsOndcFaSettled(state, action) {
      state.isOndcFaSettled = action.payload;
    },
    updateDeliveryStatus(state, action) {
      state.deliveryStatus = action.payload;
    },
    updatePaymentType(state, action) {
      state.paymentType = action.payload;
    },
    updatePaymentMode(state, action) {
      state.paymentMode = action.payload;
    },
    updateCustomerName(state, action) {
      state.customerName = action.payload;
    },
    updateCustomerPhone(state, action) {
      state.customerPhone = action.payload;
    },
    updateDeliveryUserId(state, action) {
      state.deliveryUserId = action.payload;
    },
    updateIsHeavyItemPresent(state, action) {
      state.isHeavyItemPresent = action.payload;
    },
    updateIsDeliveryLocationUpdated(state, action) {
      state.isDeliveryLocationUpdated = action.payload;
    },
    updateItemId(state, action) {
      state.itemId = action.payload;
    },
    updateRating(state, action) {
      state.rating = action.payload;
    },
    updateSelectedProductDetails(state, action) {
      state.selectedProductDetails = action.payload;
    },
    updateOrderStatusDetails(state, action) {
      state.orderDetails.orderStatusDetails = action.payload;
    },
    updateCustomerAddresses(state, action) {
      state.orderDetails.customerAddresses = action.payload;
    },
    // Per-shop shopConfig cache
    setShopConfigLoading(
      state,
      action: PayloadAction<{ shopId: string }>
    ) {
      const { shopId } = action.payload;
      const prev = state.configByShopId[shopId];
      state.configByShopId[shopId] = {
        loading: true,
        data: prev?.data ?? null,
        error: prev?.error ?? null,
        fetchedAt: prev?.fetchedAt ?? null,
      };
    },
    setShopConfigSuccess(
      state,
      action: PayloadAction<{ shopId: string; data: ShopConfig | null }>
    ) {
      const { shopId, data } = action.payload;
      state.configByShopId[shopId] = {
        loading: false,
        data,
        error: null,
        fetchedAt: Date.now(),
      };
    },
    setShopConfigError(
      state,
      action: PayloadAction<{ shopId: string; error: string }>
    ) {
      const { shopId, error } = action.payload;
      const prev = state.configByShopId[shopId];
      state.configByShopId[shopId] = {
        loading: false,
        // Preserve last known good data on failure (R6.2).
        data: prev?.data ?? null,
        error,
        // Don't touch fetchedAt — the stored data is still whatever the
        // last successful fetch produced.
        fetchedAt: prev?.fetchedAt ?? null,
      };
    },
    // Per-shop shopDetails cache
    setShopDetailsLoading(
      state,
      action: PayloadAction<{ shopId: string }>
    ) {
      const { shopId } = action.payload;
      const prev = state.shopDetailsByShopId[shopId];
      state.shopDetailsByShopId[shopId] = {
        loading: true,
        data: prev?.data ?? null,
        error: prev?.error ?? null,
        fetchedAt: prev?.fetchedAt ?? null,
      };
    },
    setShopDetailsSuccess(
      state,
      action: PayloadAction<{ shopId: string; data: ShopDetails | null }>
    ) {
      const { shopId, data } = action.payload;
      state.shopDetailsByShopId[shopId] = {
        loading: false,
        data,
        error: null,
        fetchedAt: Date.now(),
      };
    },
    setShopDetailsError(
      state,
      action: PayloadAction<{ shopId: string; error: string }>
    ) {
      const { shopId, error } = action.payload;
      const prev = state.shopDetailsByShopId[shopId];
      state.shopDetailsByShopId[shopId] = {
        loading: false,
        // Preserve last known good data on failure (R6.2).
        data: prev?.data ?? null,
        error,
        fetchedAt: prev?.fetchedAt ?? null,
      };
    },
    updateShopId(state, action) {
      state.shopId = action.payload;
    },
    setDeliveryAgentsForShop(state, action) {
      const { shopId, agents } = action.payload;
      state.deliveryAgents[shopId] = agents;
    },
    updateNewOrdersCount(state, action) {
      state.newOrdersCount = action.payload;
    },
    updateSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
  },
});

const fetchAllOrdersData = createAsyncSaga(
  "fetchAllOrders",
  Slice,
  orderService.fetchAllOrders,
  [
    "currentPage",
    "itemsPerPage",
    "state",
    "externalOrderId",
    "bapId",
    "shopName",
    "deliveryPincode",
    "paymentTransactionId",
    "shopType",
    "startDate",
    "endDate",
    "isSettled",
    "isOndcFaSettled",
    "deliveryStatus",
    "paymentType",
    "paymentMode",
    "customerName",
    "customerPhone",
    "deliveryUserId",
    "isHeavyItemPresent",
    "isDeliveryLocationUpdated",
    "itemId",
    "rating",
    "created_at",
    "modified_at",
    "shopId",
    "searchTerm",
  ]
);

addSaga(Slice.actions.fetchAllOrders.type, fetchAllOrdersData);

export const {
  fetchAllOrders,
  setPreFilledData,
  setOrderApiData,
  updateState,
  updateExternalOrderId,
  updateBapId,
  updateShopName,
  updateDeliveryPincode,
  updatePaymentTransactionId,
  updateShopType,
  updateCreatedAt,
  updateModifiedAt,
  updateFilterButton,
  updateOrderIdData,
  updateStartDate,
  updateEndDate,
  updateDateFilterButton,
  updateOrderStatuses,
  updateCancelReturnReason,
  updateIsSettled,
  setCurrentPage,
  setSelectedRowKeys,
  updateIsOndcFaSettled,
  updateDeliveryStatus,
  updatePaymentType,
  updatePaymentMode,
  updateCustomerName,
  updateCustomerPhone,
  updateDeliveryUserId,
  updateIsHeavyItemPresent,
  updateIsDeliveryLocationUpdated,
  updateItemId,
  updateRating,
  updateSelectedProductDetails,
  updateOrderStatusDetails,
  updateCustomerAddresses,
  updateShopId,
  setDeliveryAgentsForShop,
  updateNewOrdersCount,
  updateSearchTerm,
  setShopConfigLoading,
  setShopConfigSuccess,
  setShopConfigError,
  setShopDetailsLoading,
  setShopDetailsSuccess,
  setShopDetailsError,
} = Slice.actions;

export default Slice.reducer;
